// =============================================================================
// Inventory Management — the pallet register.
//
// This is the screen that replaces the spreadsheet tab people live in, so it keeps that tab's column
// order and adds the two things a spreadsheet could not: a drawer carrying each pallet's receipt,
// serials and history, and assignment that cannot be applied twice to the same stock.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { Checkbox } from '/imports/ui/Checkbox';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  InventoryPalletRow, InventoryPalletStatus, InventorySiteRow,
  inventoryAssignPallets, inventoryFeocStatusLabel, inventoryListPallets, inventoryListSites, inventoryListWarehouses,
  inventoryListLots, inventoryManualPalletStatuses, inventoryPalletDetail, inventoryPalletStatusLabel,
  inventoryPalletStatuses, inventoryPalletValue, inventoryRenamePallet, inventoryRequestDelivery,
  inventorySetPalletStatus, inventoryUnassignPallets,
} from '/imports/api/inventory';
import {
  AllocationChip, DeliveryStatusChip, EmptyRow, Fact, InventoryPageStyled, PalletStatusChip,
  count, day, money, orDash, stamp, useInventoryAccess,
} from '/imports/ui/InventoryShared';

const PalletsPageStyled = styled(InventoryPageStyled)`
  .reg { display: flex; gap: 1rem; align-items: flex-start; }
  .reg > .list { flex: 1 1 auto; min-width: 0; }
  .drawer {
    flex: 0 0 24rem; max-width: 24rem; border: 1px solid ${css.grey7}; border-radius: ${css.borderRadius};
    padding: 0.75rem 0.9rem; position: sticky; top: 0; max-height: 80vh; overflow-y: auto;
  }
  .drawer h2 { font-size: 1rem; margin: 0 0 0.1rem; }
  .drawer h3 { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey3}; margin: 0.9rem 0 0.35rem; }
  .drawer .close { float: right; }
  .drawer .actions { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.75rem; }
  .pill { font-size: 0.7rem; color: ${css.grey4}; border: 1px solid ${css.grey6}; border-radius: 999px; padding: 0 0.35rem; margin-left: 0.3rem; }
  td.sel, th.sel { width: 1.6rem; padding-right: 0; }
`;

const AssignModalStyled = styled(ModalStyled)`
  .modal-window { width: 52rem; max-width: 100%; }
  .picker { max-height: 26rem; overflow-y: auto; }
  table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  th, td { padding: 0.35rem 0.5rem; text-align: left; border-bottom: 1px solid ${css.grey7}; white-space: nowrap; }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: ${css.grey9}; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1rem; }
  .muted { color: ${css.grey4}; }
  .warn { color: ${css.red5}; font-size: 0.8rem; margin-top: 0.4rem; }
`;

const PAGE_SIZE = 100;

/** Picks the site a batch of pallets is going to. */
const AssignModal = (props: { palletCount: number; onPick: (siteId: string, force: boolean) => void; onClose: () => void }) => {
  const [search, setSearch] = useState('');
  const [sites, loading] = useMethod(inventoryListSites, { search, limit: 60 }, [search]);
  const [pending, setPending] = useState<InventorySiteRow>();
  return (
    <Modal styled={AssignModalStyled} title={strings.InventoryAssignToSite()} onClose={props.onClose}>
      <Input className="search" value={search} placeholder={strings.InventorySearchSitesPlaceholder()} onChange={(v) => setSearch(v || '')} />
      <div className="picker">
        <table>
          <thead>
            <tr>
              <th>{strings.InventorySiteId()}</th>
              <th>{strings.InventoryPropertyName()}</th>
              <th>{strings.InventoryAllocationStatus()}</th>
              <th className="num">{strings.InventoryModulesRequired()}</th>
              <th className="num">{strings.InventoryModulesAllocated()}</th>
              <th className="num">{strings.InventoryModuleBalance()}</th>
              <th>{strings.InventoryClosestWarehouse()}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7}><LoadingFlex /></td></tr>}
            {!loading && (sites?.rows || []).map((s) => (
              <tr key={s._id} onClick={() => setPending(s)}>
                <td>{s.siteId}</td>
                <td>{[s.tenant, s.propertyName].filter(Boolean).join(' — ')}</td>
                <td><AllocationChip status={s.allocationStatus} /></td>
                <td className="num">{count(s.modulesRequired)}</td>
                <td className="num">{count(s.modulesAllocated)}</td>
                <td className="num">{count(s.moduleBalance)}</td>
                <td className="muted">{orDash(s.closestWarehouseCode)}</td>
              </tr>
            ))}
            {!loading && !sites?.rows.length && <tr><td className="muted" colSpan={7}>{strings.InventoryNoSites()}</td></tr>}
          </tbody>
        </table>
      </div>
      {pending && (
        <>
          <div className="warn">
            {pending.siteId} — {pending.propertyName}
            {(pending.projectStatus === 'onHold' || pending.projectStatus === 'cancelled') && ` · ${strings.InventorySiteInactiveWarning()}`}
          </div>
          <div className="modal-buttons">
            <Button className="secondary" onClick={() => setPending(undefined)}>{strings.Cancel()}</Button>
            <Button className="primary" onClick={() => props.onPick(pending.siteId, pending.projectStatus === 'onHold' || pending.projectStatus === 'cancelled')}>
              {strings.InventoryAssignSelected()} ({props.palletCount})
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

/** One pallet in full: what it is, where it came from, what is on it and what has happened to it. */
const PalletDrawer = (props: { palletId: string; canEdit: boolean; onClose: () => void; onChanged: () => void }) => {
  const { palletId, canEdit } = props;
  const [tick, setTick] = useState(0);
  const [detail, loading] = useMethod(inventoryPalletDetail, { palletId }, [palletId, tick]);
  const [renaming, setRenaming] = useState<string>();
  const reload = () => { setTick((t) => t + 1); props.onChanged(); };

  const setStatus = toastOnError(async (status: InventoryPalletStatus) => {
    await inventorySetPalletStatus.call({ palletIds: [palletId], status });
    reload();
  });
  const rename = toastOnError(async () => {
    if (!renaming?.trim()) return;
    await inventoryRenamePallet.call({ palletId, palletNumber: renaming.trim() });
    setRenaming(undefined);
    reload();
  });
  const unassign = toastOnError(async () => {
    await inventoryUnassignPallets.call({ palletIds: [palletId] });
    reload();
  });

  if (loading && !detail) return <div className="drawer"><LoadingFlex /></div>;
  if (!detail) return null;
  const { pallet, lot, warehouse, site, delivery, modules, events, flash } = detail;

  return (
    <div className="drawer">
      <Button className="secondary compact close" onClick={props.onClose}>{strings.Close()}</Button>
      <h2>{pallet.palletNumber}{pallet.provisional && <span className="pill">{strings.InventoryProvisional()}</span>}</h2>
      <div className="muted">{[pallet.moduleModel, pallet.wattage ? `${pallet.wattage} W` : ''].filter(Boolean).join(' · ')}</div>
      <div style={{ marginTop: '0.4rem' }}><PalletStatusChip status={pallet.status} /></div>

      <h3>{strings.InventoryLot()}</h3>
      <div className="facts">
        <Fact label={strings.InventoryPo()} value={lot ? <Link to={`/inventory/lots/${lot._id}`}>{lot.poNumber || lot.contractNumber}</Link> : undefined} />
        <Fact label={strings.InventoryContractNumber()} value={pallet.contractNumber} />
        <Fact label={strings.InventoryWarehouse()} value={warehouse ? <Link to={`/inventory/warehouses/${warehouse._id}`}>{warehouse.code}</Link> : undefined} />
        <Fact label={strings.InventoryModules()} value={count(pallet.modules)} />
        <Fact label={strings.InventoryUnitCost()} value={pallet.unitCost == null ? strings.InventoryCostBasisUnknown() : money(pallet.unitCost, true)} />
        <Fact label={strings.InventoryPalletValue()} value={pallet.unitCost == null ? undefined : money(inventoryPalletValue(pallet), true)} />
        <Fact label={strings.InventoryDomesticContent()} value={pallet.domesticContentPct == null ? undefined : `${(pallet.domesticContentPct * 100).toFixed(1)}%`} />
        <Fact label={strings.InventoryFeocStatus()} value={inventoryFeocStatusLabel(pallet.feocStatus)} />
        <Fact label={strings.InventoryWarrantyExpiration()} value={pallet.warrantyExpiration ? day(pallet.warrantyExpiration) : undefined} />
      </div>

      <h3>{strings.InventoryReceipt()}</h3>
      {pallet.receipt ? (
        <div className="facts">
          <Fact label={strings.InventoryTransNumber()} value={pallet.receipt.transNumber} />
          <Fact label={strings.InventoryDateReceived()} value={day(pallet.receipt.dateReceived)} />
          <Fact label={strings.InventoryShipmentRef()} value={pallet.receipt.shipmentRef} />
          <Fact label={strings.InventoryContainer()} value={pallet.receipt.container} />
          <Fact label={strings.InventoryBol()} value={pallet.receipt.bol} />
          <Fact label={strings.InventoryLbs()} value={count(pallet.receipt.lbs)} />
        </div>
      ) : <div className="muted">{strings.NoResults()}</div>}

      <h3>{strings.InventoryAssignedSite()}</h3>
      {site ? (
        <div className="facts">
          <Fact label={strings.InventorySiteId()} value={<Link to={`/inventory/sites/${site.siteId}`}>{site.siteId}</Link>} />
          <Fact label={strings.InventoryPropertyName()} value={[site.tenant, site.propertyName].filter(Boolean).join(' — ')} />
          <Fact label={strings.InventoryAssignDate()} value={day(pallet.assignedAt)} />
          <Fact label={strings.InventoryRequestedBy()} value={pallet.assignedByName} />
          <Fact label={strings.InventoryDelivery()} value={delivery ? <DeliveryStatusChip status={delivery.status} /> : undefined} />
        </div>
      ) : <div className="muted">{strings.InventoryAllocationUnallocated()}</div>}

      <h3>{strings.InventoryFlashTest()}</h3>
      {flash ? (
        <div className="facts">
          <Fact label={strings.InventorySerialsOnFile()} value={strings.InventorySerialsOf(flash.count, pallet.modules || 0)} />
          <Fact label={strings.InventoryMeanPmax()} value={`${flash.meanPmax} W`} />
          <Fact label={strings.InventoryFlashPmax()} value={`${flash.minPmax} – ${flash.maxPmax} W`} />
          <Fact label={strings.InventoryBelowNameplate()} value={count(flash.belowNameplate)} />
        </div>
      ) : <div className="muted">{strings.InventoryNoSerials()}</div>}
      {!!modules.length && (
        <div className="scroll-x" style={{ maxHeight: '12rem', overflowY: 'auto' }}>
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryPosition()}</th>
                <th>{strings.InventorySerialNumber()}</th>
                <th className="num">{strings.InventoryFlashPmax()}</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m._id}>
                  <td>{m.position}</td>
                  <td>{m.serial}</td>
                  <td className="num">{m.flash?.pmax ?? orDash(undefined)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!pallet.notes && <><h3>{strings.Notes()}</h3><div className="muted">{pallet.notes}</div></>}

      <h3>{strings.InventoryViewEvents()}</h3>
      {events.length ? (
        <table className="inv">
          <tbody>
            {events.map((e) => (
              <tr key={e._id}>
                <td>{e.label || e.kind}</td>
                <td className="muted">{e.byName}</td>
                <td className="muted">{stamp(e.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="muted">{strings.InventoryNoEvents()}</div>}

      {canEdit && (
        <div className="actions">
          {renaming == null
            ? <Button className="secondary compact" onClick={() => setRenaming(pallet.palletNumber)}>{strings.InventoryRenamePallet()}</Button>
            : <>
              <Input value={renaming} onChange={(v) => setRenaming(v || '')} onEnter={() => rename()} />
              <Button className="primary compact" onClick={() => rename()}>{strings.Save()}</Button>
              <Button className="secondary compact" onClick={() => setRenaming(undefined)}>{strings.Cancel()}</Button>
            </>}
          {pallet.assignedSiteId && !pallet.deliveryId && <Button className="secondary compact" onClick={() => unassign()}>{strings.InventoryUnassign()}</Button>}
          {inventoryManualPalletStatuses.filter((s) => s !== pallet.status).map((s) => (
            <Button key={s} className="secondary compact" onClick={() => setStatus(s)}>{inventoryPalletStatusLabel(s)}</Button>
          ))}
        </div>
      )}
    </div>
  );
};

/** The pallet register: every pallet the org holds, filterable, with bulk assignment. */
export const InventoryPalletsPage = () => {
  const { canAccess, canEdit } = useInventoryAccess();
  const { _id } = useParams<{ _id?: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; body: string; run: () => void }>();
  const [tick, setTick] = useState(0);

  const lotId = params.get('lot') || '';
  const warehouseId = params.get('warehouse') || '';
  const status = params.get('status') || '';
  const assigned = params.get('assigned') || 'any';
  const siteId = params.get('site') || '';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(0);
  };

  const [warehouses] = useMethod(inventoryListWarehouses, undefined, []);
  const [lots] = useMethod(inventoryListLots, undefined, []);
  const [data, loading] = useMethod(
    inventoryListPallets,
    { lotId: lotId || undefined, warehouseId: warehouseId || undefined, status: (status || undefined) as any,
      assigned: assigned as any, siteId: siteId || undefined, search: search || undefined,
      skip: page * PAGE_SIZE, limit: PAGE_SIZE },
    [lotId, warehouseId, status, assigned, siteId, search, page, tick],
  );
  const reload = () => { setTick((t) => t + 1); setSelected([]); };
  useEffect(() => { setSelected([]); }, [lotId, warehouseId, status, assigned, siteId, search, page]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const pages = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE));
  const allChecked = !!rows.length && rows.every((r) => selected.includes(r._id));
  const toggleAll = () => setSelected(allChecked ? [] : rows.map((r) => r._id));
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const assign = toastOnError(async (site: string, force: boolean) => {
    const result = await inventoryAssignPallets.call({ palletIds: selected, siteId: site, force });
    toast.success(strings.InventoryAssignedPallets(result.assigned, site));
    setAssigning(false);
    reload();
  });
  const unassign = toastOnError(async () => {
    const result = await inventoryUnassignPallets.call({ palletIds: selected });
    toast.success(strings.InventoryUnassignedPallets(result.skipped ? selected.length - result.skipped : selected.length));
    reload();
  });
  const requestDelivery = toastOnError(async () => {
    const sites = [...new Set(rows.filter((r) => selected.includes(r._id)).map((r) => r.assignedSiteId))].filter(Boolean);
    if (sites.length !== 1) { toast.info(strings.InventorySelectSiteFirst()); return; }
    await inventoryRequestDelivery.call({ siteId: sites[0] as string, palletIds: selected });
    toast.success(strings.InventoryDeliveryAdvanced(strings.InventoryDeliveryRequested()));
    reload();
  });

  if (!canAccess) return <NotPermittedPage />;

  const openPallet = (row: InventoryPalletRow) => navigate(`/inventory/pallet-inventory/${row._id}${params.toString() ? `?${params}` : ''}`);

  return (
    <PalletsPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryPalletInventory()}</h1>} />
      <PageBody>
        <div className="inv-bar">
          <Input className="search" value={search} placeholder={strings.InventorySearchPalletsPlaceholder()} onChange={(v) => { setSearch(v || ''); setPage(0); }} />
          <Select
            value={lotId}
            options={[{ value: '', label: strings.InventoryAllLots() }, ...(lots || []).map((l) => ({ value: l._id, label: l.poNumber ? `${strings.InventoryPo()} ${l.poNumber}` : l.contractNumber }))]}
            onChange={(v) => setParam('lot', v)}
          />
          <Select
            value={warehouseId}
            options={[{ value: '', label: strings.InventoryAllWarehouses() }, ...(warehouses || []).map((w) => ({ value: w._id, label: `${w.code} — ${w.city}` }))]}
            onChange={(v) => setParam('warehouse', v)}
          />
          <Select
            value={status}
            options={[{ value: '', label: strings.InventoryAllStatuses() }, ...inventoryPalletStatuses.map((s) => ({ value: s, label: inventoryPalletStatusLabel(s) }))]}
            onChange={(v) => setParam('status', v)}
          />
          <Select
            value={assigned}
            options={[
              { value: 'any', label: strings.InventoryAllPallets() },
              { value: 'unassigned', label: strings.InventoryUnassignedOnly() },
              { value: 'assigned', label: strings.InventoryAssignedOnly() },
            ]}
            onChange={(v) => setParam('assigned', v === 'any' ? '' : v)}
          />
          <span className="spacer" />
          <span className="meta">
            {strings.InventoryShowingCount(data?.count || 0, data?.totalCount || 0)}
            {!!selected.length && ` · ${strings.InventorySelectedCount(selected.length)}`}
          </span>
          {canEdit && !!selected.length && <>
            <Button className="primary compact" onClick={() => setAssigning(true)}>{strings.InventoryAssignSelected()}</Button>
            <Button className="secondary compact" onClick={() => setConfirm({
              title: strings.InventoryUnassignSelected(), body: strings.InventorySelectedCount(selected.length), run: () => unassign(),
            })}>{strings.InventoryUnassignSelected()}</Button>
            <Button className="secondary compact" onClick={() => requestDelivery()}>{strings.InventoryRequestDelivery()}</Button>
          </>}
          {!!selected.length && <Button className="secondary compact" onClick={() => setSelected([])}>{strings.InventoryClearSelection()}</Button>}
        </div>

        <div className="reg">
          <div className="list">
            <div className="scroll-x">
              <table className="inv">
                <thead>
                  <tr>
                    {canEdit && <th className="sel"><Checkbox checked={allChecked} onChange={toggleAll} /></th>}
                    <th>{strings.InventoryPalletNumber()}</th>
                    <th>{strings.InventoryPo()}</th>
                    <th>{strings.InventoryWarehouse()}</th>
                    <th>{strings.Status()}</th>
                    <th className="num">{strings.InventoryModules()}</th>
                    <th className="num">{strings.InventorySerialsOnFile()}</th>
                    <th className="num">{strings.InventoryUnitCost()}</th>
                    <th className="num">{strings.InventoryPalletValue()}</th>
                    <th>{strings.InventorySiteId()}</th>
                    <th>{strings.InventoryPropertyName()}</th>
                    <th>{strings.InventoryAssignDate()}</th>
                    <th>{strings.InventoryDelivery()}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && !rows.length && <tr><td colSpan={13}><LoadingFlex /></td></tr>}
                  {rows.map((r) => (
                    <tr key={r._id} className="clickable" onClick={() => openPallet(r)}>
                      {canEdit && (
                        <td className="sel" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selected.includes(r._id)} onChange={() => toggle(r._id)} />
                        </td>
                      )}
                      <td>{r.palletNumber}{r.provisional && <span className="pill">{strings.InventoryProvisional()}</span>}</td>
                      <td>{orDash(r.poNumber)}</td>
                      <td>{orDash(r.warehouseCode)}</td>
                      <td><PalletStatusChip status={r.status} /></td>
                      <td className="num">{count(r.modules)}</td>
                      <td className="num">{(r.serialCount || 0) < (r.modules || 0)
                        ? <span className="neg">{count(r.serialCount || 0)}</span> : count(r.serialCount || 0)}</td>
                      <td className="num">{r.unitCost == null ? orDash(undefined) : money(r.unitCost, true)}</td>
                      <td className="num">{r.unitCost == null ? orDash(undefined) : money(inventoryPalletValue(r))}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {r.assignedSiteId ? <Link to={`/inventory/sites/${r.assignedSiteId}`}>{r.assignedSiteId}</Link> : orDash(undefined)}
                      </td>
                      <td>{orDash(r.siteName)}</td>
                      <td>{day(r.assignedAt)}</td>
                      <td>{r.deliveryStatus ? <DeliveryStatusChip status={r.deliveryStatus} /> : orDash(undefined)}</td>
                    </tr>
                  ))}
                  {!loading && !rows.length && <EmptyRow colSpan={13} label={strings.InventoryNoPallets()} />}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <div className="inv-bar">
                <Button className="secondary compact" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{strings.InventoryPreviousPage()}</Button>
                <Button className="secondary compact" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>{strings.InventoryNextPage()}</Button>
                <span className="meta">{page + 1} / {pages}</span>
              </div>
            )}
          </div>
          {_id && (
            <PalletDrawer
              palletId={_id}
              canEdit={canEdit}
              onClose={() => navigate(`/inventory/pallet-inventory${params.toString() ? `?${params}` : ''}`)}
              onChanged={reload}
            />
          )}
        </div>
      </PageBody>

      {assigning && <AssignModal palletCount={selected.length} onPick={(site, force) => assign(site, force)} onClose={() => setAssigning(false)} />}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          onConfirm={() => { confirm.run(); setConfirm(undefined); }}
          onClose={() => setConfirm(undefined)}
        />
      )}
    </PalletsPageStyled>
  );
};
